import { type FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type InjectedFormProps } from "redux-form";
import { ActionButton } from "widgets";

import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

import { type IConfiguredPlaybook } from "../../../app-utils/playbook.js";
import {
  createHiddenAttributesWarning,
  createLockedSamplesWarning,
  isAttributePartOfEditingGrid,
} from "../../../app-utils/sample.js";
import { pluralizeWord } from "../../../app-utils/string.js";
import { type Attribute, type InputTableQuerySpec, type SampleType } from "../../../graphql.js";
import { type IEditable } from "../../../redux/modules/data-editing.js";
import { getOrderedListOfNfTrackerAttributes } from "../../../redux/modules/playbook-execution.js";
import {
  type IAttributesTemplatesLookup,
  loadSampleDataFromAnnotationColumns,
  uniquenessValidations,
  updateSampleEditingState,
} from "../../../redux/modules/sample-editing.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { ModalActionWidget } from "../../core/overlays/index.js";
import { useCountInvalidCells } from "../phenotype/EditingGrid.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import SampleEditingGrid, { validateNgsDataFiles } from "../sample/SampleEditingGrid.js";
import SelectSamplesDialog from "../sample/SelectSamplesDialog.js";

type INfPlaybookSamplesScreenProps = IPlaybookWizardStepProvidedProps &
  InjectedFormProps<FormData, any> &
  IPlaybookWizardStepInjectedProps;

const NfPlaybookSamplesScreen: FC<INfPlaybookSamplesScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState<string>(null);
  const [validating, setValidating] = useState<boolean>(false);

  const attributes = useSelector<IAppState, Attribute[]>((state) => state.sampleEditingState.sampleAttributes);

  const sampleEditingTableQuerySpec = useSelector<IAppState, InputTableQuerySpec>(
    (state) => state.sampleEditingState.sampleEditingTableQuerySpec,
  );
  const selectedSampleType = useSelector<IAppState, SampleType>((state) => state.sampleEditingState.sampleType);
  const playbook = useSelector<IAppState, IConfiguredPlaybook>((state) => state.nfPlaybookExecution.playbook); // DIFF !!!!!!!!!
  const editingGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const templatesLookup = useSelector<IAppState, IAttributesTemplatesLookup>(
    (state) =>
      state.sampleEditingState.sampleType &&
      state.sampleEditingState.templatesLookup[state.sampleEditingState.sampleType.id],
  );

  useEffect(() => {
    if (!sampleEditingTableQuerySpec) {
      return;
    }

    // load editing data
    const mandatoryAttributeIds: number[] = playbook.nfConfig.trackerParameters.sampleAttributes
      .filter((i) => i.correctedMandatory || !!i.workflowParam)
      .map((i) => i.attributeId);

    dispatch(
      loadSampleDataFromAnnotationColumns(selectedSampleType.id, sampleEditingTableQuerySpec, mandatoryAttributeIds),
    ).catch((error: Error) => {
      props.updateWizardMessage(error.message);
    });
  }, [sampleEditingTableQuerySpec]);

  const onSubmit = () => {
    if (formErrorMessage) {
      return;
    }
    setSubmissionErrorMessage(null);
    setValidating(true);

    dispatch(uniquenessValidations())
      .then(() => setValidating(false))
      .then(props.proceedWithNextStep)
      .catch((error: Error) => {
        setSubmissionErrorMessage(error.message);
        setValidating(false);
      });
  };

  const visibleAttributes: Attribute[] = useMemo(() => {
    // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data
    return getOrderedListOfNfTrackerAttributes(playbook.nfConfig.trackerParameters.sampleAttributes, attributes).filter(
      (a) => isAttributePartOfEditingGrid(a, templatesLookup),
    );
  }, [attributes]);
  const invalidCellsCount: number = useCountInvalidCells(editingGrid);
  const multiValueWarning: string = createHiddenAttributesWarning(attributes);

  const lockedSamples: IEditable[] = editingGrid.filter((r) => r.locked);
  const lockedSamplesWarning: string | null = createLockedSamplesWarning(lockedSamples?.length);

  useEffect(() => {
    if (!editingGrid.length) {
      setFormErrorMessage("Choose one or more samples to use with this analysis");
      return;
    }

    if (invalidCellsCount > 0) {
      setFormErrorMessage(
        `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
          "is",
          invalidCellsCount,
        )} invalid.`,
      );
      return;
    }

    const ngsDataFilesValidationError = validateNgsDataFiles(playbook, attributes, editingGrid);
    if (ngsDataFilesValidationError) {
      setFormErrorMessage(ngsDataFilesValidationError);
      return;
    }

    setFormErrorMessage(null);
    setSubmissionErrorMessage(null);
  }, [editingGrid, invalidCellsCount, playbook, attributes]);

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description:
          "Choose one or more samples from the database to use with this analysis. The results from the analysis will be associated with the samples that you select.",
      }}
      wizardFooterData={{
        error: formErrorMessage || submissionErrorMessage,
        nextButtonProps: {
          label: "Next",
          isLoading: validating,
          onPress: onSubmit,
          isDisabled: !!formErrorMessage,
        },
      }}
    >
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", rowGap: 15 }}>
        <div>
          <ModalActionWidget
            actionWidget={({ open }) => <ActionButton onPress={() => open()}>Select Samples</ActionButton>}
          >
            {({ close }) => (
              <SelectSamplesDialog
                setSampleEditingSpec={(tableSpec: InputTableQuerySpec) =>
                  dispatch(updateSampleEditingState({ sampleEditingTableQuerySpec: tableSpec }))
                }
                initialSelection={editingGrid.map((row) => row.id as undefined as number)}
                sampleType={selectedSampleType}
                close={close}
              />
            )}
          </ModalActionWidget>
        </div>

        {multiValueWarning && <FormWarning>{multiValueWarning}</FormWarning>}
        {lockedSamplesWarning && <FormWarning>{lockedSamplesWarning}</FormWarning>}
        <SampleEditingGrid attributes={visibleAttributes} templatesLookup={templatesLookup} />
      </div>
    </PlaybookWizardStep>
  );
};

export default NfPlaybookSamplesScreen;
